import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listingTableSx } from '../components/listingTableSx';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Psychology as AIIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  InfoOutlined as InfoOutlinedIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  SmartToy as SmartToyIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Inventory2 as InventoryIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import { aiGovernanceAPI, teamContactsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { UI_TOKENS } from '../constants/uiTokens';
import { BTN_SM, CARD_SX, FIELD_SX, MENU_PROPS, PAGE, SELECT_SX, TABS_SX } from '../constants/pageStyles';
import type { User } from '../types';
import AiGovernanceAccessPanel from '../components/aiGovernance/AiGovernanceAccessPanel';
import AiGovernanceCapabilitiesToolsPanel from '../components/aiGovernance/AiGovernanceCapabilitiesToolsPanel';
import AiGovernanceSettingsPanel from '../components/aiGovernance/AiGovernanceSettingsPanel';
import AiGovernanceDashboardPanel from '../components/aiGovernance/AiGovernanceDashboardPanel';
import { ListingPagination, ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';
import { getApiErrorMessage } from '../utils/apiErrors';
import {
  chipSxFromSurface,
  getEnvironmentSurface,
  getLifecycleStatusSurface,
  getRiskSurface,
  getStatusSurface,
} from '../utils/appearanceTokens';
import type {
  AIAsset,
  AIAssetActivationReadiness,
  AIAssetRiskAssessment,
  AIGovernanceEnums,
  CreateAIAssetRequest
} from '../types/aiGovernance';

const AGENT_TYPES = new Set(['AI Agent', 'Copilot', 'AI Automation']);

const TABLE_HEADER_SX = {
  fontWeight: 700,
  fontSize: UI_TOKENS.text.labelXs,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
  color: 'text.secondary',
  py: 1.25,
  whiteSpace: 'nowrap' as const,
  bgcolor: 'grey.50',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const TABLE_COLUMNS = [
  { label: 'System ID', width: '10%' },
  { label: 'Owner ID', width: '11%' },
  { label: 'Name', width: '16%' },
  { label: 'Type', width: '11%' },
  { label: 'Application', width: '14%' },
  { label: 'Environment', width: '9%' },
  { label: 'Status', width: '9%' },
  { label: 'Risk', width: '8%' },
  { label: '', width: '6%' },
];

function getStatusChipStyle(status: string, mode: 'light' | 'dark') {
  return getLifecycleStatusSurface(status, mode);
}

function getEnvChipStyle(env: string, mode: 'light' | 'dark') {
  return getEnvironmentSurface(env, mode);
}

function getRiskChipStyle(level: string | undefined, mode: 'light' | 'dark') {
  return getRiskSurface(level || '', mode);
}

function renderStyledChip(label: string, style: { bg: string; fg: string; border: string }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: UI_TOKENS.text.helper,
        fontWeight: 600,
        ...chipSxFromSurface(style),
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

const emptyForm = (): CreateAIAssetRequest => ({
  owner_asset_id: '',
  name: '',
  asset_type: 'AI Agent',
  linked_application_id: '',
  business_purpose: '',
  environment: 'Dev',
  business_owner_id: '',
  technical_owner_id: '',
  security_reviewer_id: '',
  data_classification: 'Internal',
  model_provider: '',
  agent_profile: {
    identity_mode: 'Own Credentials',
    autonomy_level: 'Human-in-the-loop',
    general_capability_intent: '',
    lifecycle_status: 'Proposed',
  },
});

const AIGovernance: React.FC = () => {
  const { user } = useAuth();
  const { showNotification, showError } = useNotification();
  const { colors, resolvedMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState<AIAsset[]>([]);
  const [applications, setApplications] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [enums, setEnums] = useState<AIGovernanceEnums | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssetType, setFilterAssetType] = useState('all');
  const [filterEnvironment, setFilterEnvironment] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState<AIAsset | null>(null);
  const [readiness, setReadiness] = useState<AIAssetActivationReadiness | null>(null);
  const [riskAssessment, setRiskAssessment] = useState<AIAssetRiskAssessment | null>(null);
  const [riskAnswers, setRiskAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [form, setForm] = useState<CreateAIAssetRequest>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [mainTab, setMainTab] = useState(() => {
    const tab = searchParams.get('tab');
    if (tab === 'inventory') return 1;
    if (tab === 'access') return 2;
    if (tab === 'settings') return 3;
    return 0;
  });
  const [accessRefreshToken, setAccessRefreshToken] = useState(0);
  const [settingsRefreshToken, setSettingsRefreshToken] = useState(0);
  const [dashboardRefreshToken, setDashboardRefreshToken] = useState(0);
  const [assetPage, setAssetPage] = useState(1);
  const [assetRowsPerPage, setAssetRowsPerPage] = useState(10);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);

  const accessDeepLink = useMemo(() => {
    const grantId = searchParams.get('grantId') ?? undefined;
    const appId = searchParams.get('appId') ?? undefined;
    const panel = searchParams.get('panel') === 'owner' ? 'owner' : 'requests';
    if (!grantId) return undefined;
    return { grantId, appId, panel: panel as 'owner' | 'requests' };
  }, [searchParams]);

  const clearAccessDeepLink = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('grantId');
      next.delete('appId');
      next.delete('panel');
      next.delete('tab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const canManageSettings = user?.role === 'super_admin' || user?.role === 'grc_admin';

  const navigateMainTab = useCallback(
    (tab: 'dashboard' | 'inventory' | 'access' | 'settings') => {
      const index = tab === 'dashboard' ? 0 : tab === 'inventory' ? 1 : tab === 'access' ? 2 : 3;
      setMainTab(index);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'dashboard') next.delete('tab');
        else next.set('tab', tab);
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'inventory') setMainTab(1);
    else if (tab === 'access') setMainTab(2);
    else if (tab === 'settings' && canManageSettings) setMainTab(3);
    else if (!tab || tab === 'dashboard') setMainTab(0);
  }, [searchParams, canManageSettings]);

  const userLabel = (u: User) =>
    [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (search.trim()) params.search = search.trim();
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterAssetType !== 'all') params.asset_type = filterAssetType;
      if (filterEnvironment !== 'all') params.environment = filterEnvironment;

      const [assetsRes, usersList, enumsRes, manageableRes] = await Promise.all([
        aiGovernanceAPI.listAssets(Object.keys(params).length ? params : undefined),
        teamContactsAPI.getUsers(),
        aiGovernanceAPI.getEnums(),
        aiGovernanceAPI.listManageableApplications(),
      ]);
      setAssets(assetsRes.data.data ?? []);
      setApplications(manageableRes.data.data ?? []);
      setUsers(usersList);
      setEnums(enumsRes.data.data ?? null);
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Failed to load AI governance data');
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterAssetType, filterEnvironment]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const openCreateDialog = async () => {
    setError(null);
    try {
      const usersList = await teamContactsAPI.getUsers();
      setUsers(usersList);
    } catch {
      /* keep existing list if refresh fails */
    }
    setForm({
      ...emptyForm(),
      business_owner_id: user?.id ?? '',
      technical_owner_id: user?.id ?? '',
      security_reviewer_id: user?.id ?? '',
    });
    setDialogOpen(true);
  };

  const showAgentFields = useMemo(() => AGENT_TYPES.has(form.asset_type), [form.asset_type]);
  const detailShowAgentFields = useMemo(
    () => (selectedAsset ? AGENT_TYPES.has(selectedAsset.asset_type) : false),
    [selectedAsset]
  );

  const hasActiveFilters =
    filterStatus !== 'all' || filterAssetType !== 'all' || filterEnvironment !== 'all' || search.trim() !== '';

  useEffect(() => {
    setAssetPage(1);
  }, [search, filterStatus, filterAssetType, filterEnvironment, assetRowsPerPage, listingSort]);

  const sortedAssets = sortListing(assets, listingSort);
  const assetTotalPages = Math.max(1, Math.ceil(sortedAssets.length / assetRowsPerPage));
  const assetSafePage = Math.min(Math.max(1, assetPage), assetTotalPages);
  const assetStart = (assetSafePage - 1) * assetRowsPerPage;
  const paginatedAssets = sortedAssets.slice(assetStart, assetStart + assetRowsPerPage);

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('all');
    setFilterAssetType('all');
    setFilterEnvironment('all');
  };

  const loadAssetDetail = async (asset: AIAsset) => {
    setDetailLoading(true);
    setError(null);
    try {
      const [assetRes, readinessRes, riskRes] = await Promise.all([
        aiGovernanceAPI.getAsset(asset.id),
        aiGovernanceAPI.getActivationReadiness(asset.id),
        aiGovernanceAPI.getRiskAssessment(asset.id),
      ]);
      const full = assetRes.data.data ?? asset;
      setSelectedAsset(full);
      setForm({
        owner_asset_id: full.owner_asset_id ?? '',
        name: full.name,
        asset_type: full.asset_type,
        linked_application_id: full.linked_application_id,
        business_purpose: full.business_purpose ?? '',
        environment: full.environment,
        business_owner_id: full.owners.find((o) => o.owner_role === 'business')?.user_id ?? '',
        technical_owner_id: full.owners.find((o) => o.owner_role === 'technical')?.user_id ?? '',
        security_reviewer_id: full.owners.find((o) => o.owner_role === 'security_reviewer')?.user_id ?? '',
        data_classification: full.data_classification,
        model_provider: full.model_provider ?? '',
        agent_profile: full.agent_profile
          ? {
              identity_mode: full.agent_profile.identity_mode,
              autonomy_level: full.agent_profile.autonomy_level,
              general_capability_intent: full.agent_profile.general_capability_intent ?? '',
              lifecycle_status: full.agent_profile.lifecycle_status,
            }
          : undefined,
      });
      setReadiness(readinessRes.data.data ?? null);
      const risk = riskRes.data.data ?? null;
      setRiskAssessment(risk);
      if (risk) {
        const answers: Record<string, string> = {};
        for (const [k, v] of Object.entries(risk.answers)) {
          answers[k] = String(v);
        }
        setRiskAnswers(answers);
      } else {
        setRiskAnswers({});
      }
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to load asset details'));
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = (asset: AIAsset) => {
    setDetailTab(0);
    setDetailOpen(true);
    void loadAssetDetail(asset);
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const payload: CreateAIAssetRequest = { ...form };
      if (!showAgentFields) {
        delete payload.agent_profile;
      }
      await aiGovernanceAPI.createAsset(payload);
      showNotification('AI asset created', 'success');
      setDialogOpen(false);
      setForm(emptyForm());
      await loadData();
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to create AI asset'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedAsset) return;
    setSubmitting(true);
    try {
      const payload: Partial<CreateAIAssetRequest> = { ...form };
      if (!detailShowAgentFields) {
        delete payload.agent_profile;
      }
      await aiGovernanceAPI.updateAsset(selectedAsset.id, payload);
      showNotification('AI asset updated', 'success');
      await loadData();
      await loadAssetDetail(selectedAsset);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to update AI asset'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartRisk = async () => {
    if (!selectedAsset) return;
    setSubmitting(true);
    try {
      const res = await aiGovernanceAPI.ensureRiskAssessment(selectedAsset.id);
      setRiskAssessment(res.data.data ?? null);
      showNotification('Risk questionnaire started', 'success');
      await loadAssetDetail(selectedAsset);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to start risk questionnaire'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveRiskAnswers = async () => {
    if (!selectedAsset) return;
    setSubmitting(true);
    try {
      const res = await aiGovernanceAPI.saveRiskAnswers(selectedAsset.id, riskAnswers);
      setRiskAssessment(res.data.data ?? null);
      const tier = res.data.data?.risk_level;
      showNotification(
        tier ? `Answers saved — calculated risk tier: ${tier}` : 'Risk answers saved',
        'success'
      );
      await loadAssetDetail(selectedAsset);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to save risk answers'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRisk = async () => {
    if (!selectedAsset) return;
    setSubmitting(true);
    try {
      await aiGovernanceAPI.saveRiskAnswers(selectedAsset.id, riskAnswers);
      const res = await aiGovernanceAPI.submitRiskAssessment(selectedAsset.id);
      setRiskAssessment(res.data.data ?? null);
      const tier = res.data.data?.risk_level;
      showNotification(
        tier ? `Questionnaire submitted — risk tier: ${tier}` : 'Risk questionnaire submitted',
        'success'
      );
      await loadData();
      await loadAssetDetail(selectedAsset);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to submit risk questionnaire'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedAsset) return;
    setSubmitting(true);
    try {
      await aiGovernanceAPI.activateAsset(selectedAsset.id);
      showNotification('AI asset activated', 'success');
      setDetailOpen(false);
      await loadData();
    } catch (e: unknown) {
      const resp = (e as { response?: { data?: { error?: string; blockers?: string[] } } })?.response?.data;
      const msg = resp?.blockers?.length
        ? `${resp.error}: ${resp.blockers.join('; ')}`
        : resp?.error || 'Failed to activate AI asset';
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetire = async () => {
    if (!selectedAsset) return;
    if (
      !window.confirm(
        `Retire "${selectedAsset.name}"? This will revoke all active access grants for this agent and cannot be undone.`
      )
    ) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await aiGovernanceAPI.retireAsset(selectedAsset.id);
      const revoked = (res.data as { revoked_grants?: number }).revoked_grants ?? 0;
      showNotification(
        revoked > 0 ? `Asset retired; ${revoked} grant(s) revoked` : 'Asset retired',
        'success'
      );
      setDetailOpen(false);
      await loadData();
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to retire AI asset'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderDialogTitle = (title: string, subtitle: string | undefined, onClose: () => void) => (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2.5,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
          <AIIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.25 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.3 }} noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.25 }} noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close" sx={{ mt: -0.25 }}>
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </DialogTitle>
    </>
  );

  const renderSectionHeader = (title: string, icon?: React.ReactNode) => (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 0.25 }}>
        {icon}
        <Typography sx={PAGE.sectionTitle}>{title}</Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
    </Grid>
  );

  const renderOwnerFields = () => (
    <>
      {renderSectionHeader('Ownership', <PeopleIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth required size="small" sx={SELECT_SX}>
          <InputLabel>Business Owner</InputLabel>
          <Select
            label="Business Owner"
            value={form.business_owner_id}
            onChange={(e) => setForm({ ...form, business_owner_id: e.target.value })}
            MenuProps={MENU_PROPS}
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {userLabel(u)} ({u.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth required size="small" sx={SELECT_SX}>
          <InputLabel>Technical Owner</InputLabel>
          <Select
            label="Technical Owner"
            value={form.technical_owner_id}
            onChange={(e) => setForm({ ...form, technical_owner_id: e.target.value })}
            MenuProps={MENU_PROPS}
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {userLabel(u)} ({u.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth required size="small" sx={SELECT_SX}>
          <InputLabel>Security Reviewer</InputLabel>
          <Select
            label="Security Reviewer"
            value={form.security_reviewer_id}
            onChange={(e) => setForm({ ...form, security_reviewer_id: e.target.value })}
            MenuProps={MENU_PROPS}
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {userLabel(u)} ({u.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </>
  );

  const renderAgentFields = () => (
    <>
      {renderSectionHeader('Agent Profile', <SmartToyIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth size="small" sx={SELECT_SX}>
          <InputLabel>Identity Mode</InputLabel>
          <Select
            label="Identity Mode"
            value={form.agent_profile?.identity_mode ?? 'Own Credentials'}
            onChange={(e) =>
              setForm({
                ...form,
                agent_profile: { ...form.agent_profile, identity_mode: e.target.value },
              })
            }
            MenuProps={MENU_PROPS}
          >
            {(enums?.identity_modes ?? []).map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth size="small" sx={SELECT_SX}>
          <InputLabel>Autonomy Level</InputLabel>
          <Select
            label="Autonomy Level"
            value={form.agent_profile?.autonomy_level ?? 'Human-in-the-loop'}
            onChange={(e) =>
              setForm({
                ...form,
                agent_profile: { ...form.agent_profile, autonomy_level: e.target.value },
              })
            }
            MenuProps={MENU_PROPS}
          >
            {(enums?.autonomy_levels ?? []).map((a) => (
              <MenuItem key={a} value={a}>
                {a}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="General Capability / Intent"
          fullWidth
          size="small"
          multiline
          minRows={2}
          value={form.agent_profile?.general_capability_intent ?? ''}
          onChange={(e) =>
            setForm({
              ...form,
              agent_profile: { ...form.agent_profile, general_capability_intent: e.target.value },
            })
          }
          sx={FIELD_SX}
        />
      </Grid>
    </>
  );

  const renderAssetFormFields = (includeTypeAndApp: boolean) => (
    <Grid container spacing={1.5}>
      {renderSectionHeader('Identification', <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
      <Grid item xs={12} md={6}>
        <TextField
          label="Owner Asset ID"
          required
          fullWidth
          size="small"
          helperText="Your organization's identifier (e.g. AGENT-HR-001)"
          value={form.owner_asset_id}
          onChange={(e) => setForm({ ...form, owner_asset_id: e.target.value })}
          sx={FIELD_SX}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="AI Asset Name"
          required
          fullWidth
          size="small"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          sx={FIELD_SX}
        />
      </Grid>

      {includeTypeAndApp && (
        <>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required size="small" sx={SELECT_SX}>
              <InputLabel>AI Asset Type</InputLabel>
              <Select
                label="AI Asset Type"
                value={form.asset_type}
                onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                MenuProps={MENU_PROPS}
              >
                {(enums?.asset_types ?? ['AI Agent']).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required size="small" sx={SELECT_SX}>
              <InputLabel>Linked Application</InputLabel>
              <Select
                label="Linked Application"
                value={form.linked_application_id}
                onChange={(e) => setForm({ ...form, linked_application_id: e.target.value })}
                MenuProps={MENU_PROPS}
              >
                {applications.map((app) => (
                  <MenuItem key={app.id} value={app.id}>
                    {app.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </>
      )}

      {renderSectionHeader('Details', <AssignmentIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
      <Grid item xs={12}>
        <TextField
          label="Business Purpose"
          required
          fullWidth
          size="small"
          multiline
          minRows={3}
          value={form.business_purpose}
          onChange={(e) => setForm({ ...form, business_purpose: e.target.value })}
          sx={FIELD_SX}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth size="small" sx={SELECT_SX}>
          <InputLabel>Environment</InputLabel>
          <Select
            label="Environment"
            value={form.environment ?? 'Dev'}
            onChange={(e) => setForm({ ...form, environment: e.target.value })}
            MenuProps={MENU_PROPS}
          >
            {(enums?.environments ?? ['Dev', 'Test', 'Prod']).map((env) => (
              <MenuItem key={env} value={env}>
                {env}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth size="small" sx={SELECT_SX}>
          <InputLabel>Data Classification</InputLabel>
          <Select
            label="Data Classification"
            value={form.data_classification ?? 'Internal'}
            onChange={(e) => setForm({ ...form, data_classification: e.target.value })}
            MenuProps={MENU_PROPS}
          >
            {(enums?.data_classifications ?? ['Internal']).map((dc) => (
              <MenuItem key={dc} value={dc}>
                {dc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {renderOwnerFields()}
      {showAgentFields && renderAgentFields()}

      {includeTypeAndApp && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: UI_TOKENS.text.helper } }}>
            A system ID (e.g. AIA-000001) is assigned automatically when the asset is created.
          </Alert>
        </Grid>
      )}
    </Grid>
  );

  const renderAssetRow = (asset: AIAsset, idx: number) => {
    const envStyle = getEnvChipStyle(asset.environment, resolvedMode);
    const statusStyle = getStatusChipStyle(asset.status, resolvedMode);
    const riskStyle = getRiskChipStyle(asset.risk_tier, resolvedMode);

    return (
      <TableRow
        key={asset.id}
        hover
        sx={{
          bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
          '&:hover': { bgcolor: 'action.hover' },
          '&:last-child td': { borderBottom: 0 },
        }}
      >
        <TableCell sx={{ py: 1 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontFamily: 'monospace', fontWeight: 600 }}>
            {asset.asset_code}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontFamily: 'monospace', color: 'text.secondary' }} noWrap>
            {asset.owner_asset_id || '—'}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: UI_TOKENS.text.bodySm }} noWrap>
            {asset.name}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }} noWrap>
            {asset.asset_type}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }} noWrap>
            {asset.linked_application_name || '—'}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1 }}>{renderStyledChip(asset.environment, envStyle)}</TableCell>
        <TableCell sx={{ py: 1 }}>{renderStyledChip(asset.status, statusStyle)}</TableCell>
        <TableCell sx={{ py: 1 }}>
          {asset.risk_tier ? renderStyledChip(asset.risk_tier, riskStyle) : (
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.disabled' }}>—</Typography>
          )}
        </TableCell>
        <TableCell sx={{ py: 1 }} align="right">
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => openDetail(asset)} sx={{ color: 'primary.main', p: 0.5 }}>
              <VisibilityIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={PAGE.padding}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Box>
            <Typography sx={PAGE.title}>AI Governance</Typography>
            <Typography sx={PAGE.subtitle}>
              {mainTab === 0
                ? 'Enterprise-wide metrics for AI assets, access grants, and lifecycle automation'
                : mainTab === 1
                  ? 'Register and manage AI assets — each asset has a system ID and an owner-assigned ID'
                  : mainTab === 2
                    ? 'Submit cross-app access requests and review inbound grants as an application owner'
                    : 'Configure grant expiry, recertification, and lifecycle automation for your enterprise'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
            onClick={() => {
              if (mainTab === 0) {
                setDashboardRefreshToken((t) => t + 1);
              } else if (mainTab === 1) {
                void loadData();
              } else if (mainTab === 2) {
                setAccessRefreshToken((t) => t + 1);
              } else {
                setSettingsRefreshToken((t) => t + 1);
              }
            }}
            disabled={loading && mainTab === 1}
            sx={BTN_SM}
          >
            Refresh
          </Button>
          {mainTab === 1 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={openCreateDialog}
              sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600, '&:hover': { bgcolor: colors.dark } }}
            >
              Register AI Asset
            </Button>
          )}
        </Box>
      </Box>

      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        sx={{ ...TABS_SX, mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab label="Dashboard" icon={<DashboardIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Asset Inventory" icon={<InventoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Access & Owner Console" icon={<VpnKeyIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        {canManageSettings && (
          <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Settings" />
        )}
      </Tabs>

      {error && mainTab !== 3 && (
        <Alert severity="error" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mainTab === 0 ? (
        <AiGovernanceDashboardPanel
          refreshToken={dashboardRefreshToken}
          onNavigateTab={(tab) => navigateMainTab(tab)}
        />
      ) : mainTab === 2 ? (
        <AiGovernanceAccessPanel
          assets={assets}
          enums={enums}
          refreshToken={accessRefreshToken}
          deepLink={accessDeepLink}
          onDeepLinkHandled={clearAccessDeepLink}
        />
      ) : mainTab === 3 && canManageSettings ? (
        <AiGovernanceSettingsPanel refreshToken={settingsRefreshToken} />
      ) : (
        <>
      {/* Filters */}
      <Paper variant="outlined" sx={{ ...CARD_SX, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
          <FilterListIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
          <Typography sx={PAGE.sectionTitle}>Filters</Typography>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Name, system ID, or owner ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void loadData()}
              sx={FIELD_SX}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={SELECT_SX}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All Statuses</MenuItem>
                {(enums?.asset_statuses ?? ['Draft', 'Active', 'Suspended', 'Retired']).map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={SELECT_SX}>
              <InputLabel>Asset Type</InputLabel>
              <Select label="Asset Type" value={filterAssetType} onChange={(e) => setFilterAssetType(e.target.value)} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All Types</MenuItem>
                {(enums?.asset_types ?? []).map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={SELECT_SX}>
              <InputLabel>Environment</InputLabel>
              <Select label="Environment" value={filterEnvironment} onChange={(e) => setFilterEnvironment(e.target.value)} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All Environments</MenuItem>
                {(enums?.environments ?? ['Dev', 'Test', 'Prod']).map((env) => (
                  <MenuItem key={env} value={env}>{env}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <ListingSortSelect
              value={listingSort}
              onChange={setListingSort}
              sx={SELECT_SX}
              menuProps={MENU_PROPS}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button variant="outlined" size="small" onClick={clearFilters} fullWidth sx={{ height: 40, ...BTN_SM }}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ ...CARD_SX, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontWeight: 600, color: 'text.secondary' }}>
            {loading ? 'Loading…' : `${assets.length} asset${assets.length !== 1 ? 's' : ''}${hasActiveFilters ? ' (filtered)' : ''}`}
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
          <Table stickyHeader size="small" sx={{ ...listingTableSx, tableLayout: 'fixed', width: '100%', minWidth: 960 }}>
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map(({ label, width }) => (
                  <TableCell key={label || 'actions'} sx={{ ...TABLE_HEADER_SX, width }} align={label ? 'left' : 'right'}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    {TABLE_COLUMNS.map((_, j) => (
                      <TableCell key={j} sx={{ py: 1 }}>
                        <Skeleton height={22} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : assets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMNS.length} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>
                      {hasActiveFilters ? 'No AI assets match the current filters' : 'No AI assets yet'}
                    </Typography>
                    {!hasActiveFilters && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
                        onClick={openCreateDialog}
                        sx={{ mt: 2, ...BTN_SM, bgcolor: colors.primary }}
                      >
                        Register Your First Asset
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAssets.map((asset, idx) => renderAssetRow(asset, idx))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <ListingPagination
          page={assetSafePage}
          totalPages={assetTotalPages}
          totalItems={assets.length}
          pageSize={assetRowsPerPage}
          onPageChange={setAssetPage}
          onPageSizeChange={setAssetRowsPerPage}
          pageSizeSelectSx={SELECT_SX}
          pageSizeMenuProps={MENU_PROPS}
        />
      </Paper>
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        {renderDialogTitle('Register AI Asset', 'Create a draft asset with your owner-assigned ID', () => setDialogOpen(false))}
        <DialogContent sx={{ pt: 2, px: 2.5, pb: 1 }}>
          {renderAssetFormFields(true)}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" onClick={() => setDialogOpen(false)} disabled={submitting} sx={BTN_SM}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => void handleCreate()}
            disabled={submitting}
            sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600, minWidth: 120 }}
          >
            {submitting ? <CircularProgress size={16} color="inherit" /> : 'Create Draft Asset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        {renderDialogTitle(
          selectedAsset?.name ?? 'AI Asset',
          selectedAsset ? `${selectedAsset.asset_code} · Owner ID: ${selectedAsset.owner_asset_id || '—'}` : undefined,
          () => setDetailOpen(false)
        )}
        <DialogContent sx={{ pt: 0, px: 0, pb: 0 }}>
          {detailLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : selectedAsset ? (
            <>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {renderStyledChip(selectedAsset.status, getStatusChipStyle(selectedAsset.status, resolvedMode))}
                {renderStyledChip(selectedAsset.environment, getEnvChipStyle(selectedAsset.environment, resolvedMode))}
                {selectedAsset.risk_tier && renderStyledChip(selectedAsset.risk_tier, getRiskChipStyle(selectedAsset.risk_tier, resolvedMode))}
                {selectedAsset.data_classification && (
                  <Chip
                    label={selectedAsset.data_classification}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, '& .MuiChip-label': { px: 1 } }}
                  />
                )}
                {selectedAsset.material_change_pending && (
                  <Chip
                    label="Material change pending"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: UI_TOKENS.text.helper,
                      fontWeight: 600,
                      ...chipSxFromSurface(getStatusSurface('warning', resolvedMode)),
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                )}
              </Box>

              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ ...TABS_SX, px: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Tab label="Overview" />
                <Tab label="Risk & Activation" />
                <Tab label="Inventory Extensions" />
              </Tabs>

              <Box sx={{ px: 2.5, py: 2 }}>
                {detailTab === 0 && (
                  <>
                    {selectedAsset.material_change_pending && (
                      <Alert severity="warning" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }}>
                        This asset had a material change. Active access grants require re-attestation in the Access tab.
                      </Alert>
                    )}
                    {renderAssetFormFields(false)}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                      {['Active', 'Suspended', 'Draft'].includes(selectedAsset.status) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => void handleRetire()}
                          disabled={submitting}
                          sx={BTN_SM}
                        >
                          Retire Asset
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => void handleUpdate()}
                        disabled={submitting || selectedAsset.status === 'Retired'}
                        sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600, minWidth: 110 }}
                      >
                        {submitting ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
                      </Button>
                    </Box>
                  </>
                )}

                {detailTab === 1 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }}>
                      Questions are configured by your GRC administrator (Risk Questions → framework{' '}
                      <strong>AI Governance</strong>). Risk tier is calculated automatically when you save or
                      submit answers.
                    </Alert>
                    {readiness && !readiness.can_activate && readiness.blockers.length > 0 && (
                      <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }}>
                        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700, mb: 0.5 }}>
                          Activation blockers
                        </Typography>
                        <List dense disablePadding>
                          {readiness.blockers.map((b) => (
                            <ListItem key={b} disablePadding sx={{ py: 0.25 }}>
                              <ListItemText primary={b} primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }} />
                            </ListItem>
                          ))}
                        </List>
                      </Alert>
                    )}

                    {!riskAssessment ? (
                      <Paper variant="outlined" sx={{ ...CARD_SX, p: 2.5, textAlign: 'center' }}>
                        <AssignmentIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary', mb: 2 }}>
                          Complete the AI risk questionnaire before activating this asset.
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => void handleStartRisk()}
                          disabled={submitting}
                          sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600 }}
                        >
                          Start Risk Questionnaire
                        </Button>
                      </Paper>
                    ) : (
                      <>
                        <Paper variant="outlined" sx={{ ...CARD_SX, p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>
                              Questionnaire — {riskAssessment.status}
                            </Typography>
                            <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
                              {riskAssessment.completion_percentage}% complete
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={riskAssessment.completion_percentage}
                            sx={{ mb: 1, height: 6, borderRadius: 1 }}
                          />
                          {riskAssessment.risk_level && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
                                Assessed risk tier:
                              </Typography>
                              {renderStyledChip(riskAssessment.risk_level, getRiskChipStyle(riskAssessment.risk_level, resolvedMode))}
                            </Box>
                          )}
                        </Paper>

                        {riskAssessment.questions.map((q, qIdx) => (
                          <Paper key={q.id} variant="outlined" sx={{ ...CARD_SX, p: 2 }}>
                            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600, mb: 1 }}>
                              {qIdx + 1}. {q.question_text}
                            </Typography>
                            <FormControl component="fieldset">
                              <RadioGroup
                                value={riskAnswers[q.id] ?? ''}
                                onChange={(e) => setRiskAnswers({ ...riskAnswers, [q.id]: e.target.value })}
                              >
                                {(q.response_options ?? [
                                  { value: 'yes', label: 'Yes' },
                                  { value: 'no', label: 'No' },
                                ]).map((opt) => (
                                  <FormControlLabel
                                    key={opt.value}
                                    value={opt.value}
                                    control={<Radio size="small" />}
                                    label={opt.label}
                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm } }}
                                  />
                                ))}
                              </RadioGroup>
                            </FormControl>
                          </Paper>
                        ))}

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                          <Button size="small" variant="outlined" onClick={() => void handleSaveRiskAnswers()} disabled={submitting} sx={BTN_SM}>
                            Save Answers
                          </Button>
                          {riskAssessment.status !== 'Approved' && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => void handleSubmitRisk()}
                              disabled={submitting}
                              sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600 }}
                            >
                              Submit Questionnaire
                            </Button>
                          )}
                          {readiness?.can_activate && selectedAsset.status === 'Draft' && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => void handleActivate()}
                              disabled={submitting}
                              sx={{ ...BTN_SM, fontWeight: 600 }}
                            >
                              Activate Asset
                            </Button>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                {detailTab === 2 && selectedAsset && (
                  <AiGovernanceCapabilitiesToolsPanel
                    asset={selectedAsset}
                    enums={enums}
                    readOnly={selectedAsset.status === 'Retired'}
                    onMaterialChange={() => {
                      if (selectedAsset) void loadAssetDetail(selectedAsset);
                    }}
                  />
                )}
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" onClick={() => setDetailOpen(false)} variant="contained" sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIGovernance;
